import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  auditorCategoryListGet,
  manualAuditSubmit
} from '../../../store/slices/tenantSlices/auditorSlice';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import { languageSelectOptions } from '../../../utils/common';

const ManualAuditor = () => {
  const dispatch = useDispatch();

  const initialValues = {
    audio: '',
    lang: '',
    category_id: ''
  };

  const validationSchema = Yup.object({
    audio: Yup.string().required('audio file is required'),
    lang: Yup.string().required('language is required'),
    category_id: Yup.string().required('category is required')
  });

  const [loading, setLoading] = useState(null);
  const [isReport, setIsReport] = useState(false);
  const [fileName, setFileName] = useState('');
  const [behavior, setBehavior] = useState('');
  const [auditDetails, setAuditDetails] = useState([]);
  const [totalAvgScore, setTotalAvgScore] = useState(0);
  const [callTranscript, setCallTranscript] = useState('');
  const [interestPercentage, setInterestPercentage] = useState(0);
  const [auditorCategoryOption, setAuditorCategoryOption] = useState([]);

  const isAutoAudit = process.env.REACT_APP_AUTO_AUDIT_RESULT === 'true';

  const earnedPoints = (subcategories = []) =>
    subcategories.reduce((total, { applicable, score_weight = 0 }) => {
      if (isAutoAudit) {
        return applicable === 'yes' ? total + score_weight : total;
      }
      const value = Number(applicable);
      return !isNaN(value) ? total + value : total;
    }, 0);

  const applicablePoints = (subcategories = []) =>
    subcategories.reduce((total, { applicable, score_weight = 0 }) => {
      if (isAutoAudit) {
        return applicable === 'yes' || applicable === 'no' ? total + score_weight : total;
      }
      return !isNaN(Number(applicable)) ? total + score_weight : total;
    }, 0);

  const scoreAvg = (ePoint, aPoint) => {
    if (aPoint === 0) return 0;
    return parseFloat(((ePoint / aPoint) * 100).toFixed(2));
  };

  const processedDetails = useMemo(() => {
    return auditDetails.map((item) => {
      const subcategories = item.subcategories || [];
      const totalWeightage = subcategories.reduce((sum, sub) => sum + sub.score_weight, 0);
      const ePoints = earnedPoints(subcategories);
      const aPoints = applicablePoints(subcategories);
      const score = scoreAvg(ePoints, aPoints);

      return {
        ...item,
        totalWeightage,
        ePoints,
        aPoints,
        score: parseFloat(score.toFixed(2))
      };
    });
  }, [auditDetails]);

  useEffect(() => {
    if (!processedDetails.length) return;
    const validItems = processedDetails.filter((item) => item.aPoints > 0);
    const totalScore = validItems.reduce((sum, item) => sum + item.score, 0);
    const avg = validItems.length > 0 ? parseFloat((totalScore / validItems.length).toFixed(2)) : 0;

    setTotalAvgScore(avg);
  }, [processedDetails]);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', values.audio);
      formData.append('category_id', values?.category_id);
      formData.append('lang', values?.lang);
      const response = await dispatch(manualAuditSubmit(formData)).unwrap();
      if (response && response.success) {
        const processedAuditDetails = (response?.details || []).map((category) => ({
          ...category,
          subcategories: category.subcategories.map((subcategory) => {
            const applicableDetail = response?.data?.details.find(
              (detail) => detail.auditor_sub_category_id === subcategory.subcategory_id
            );
            const isApplicable = applicableDetail ? applicableDetail.applicable : 'na';
            return {
              ...subcategory,
              applicable: isApplicable,
              feedback: subcategory?.feedback || ''
            };
          })
        }));
        setAuditDetails(processedAuditDetails);
        setCallTranscript(response?.data?.transcript || '');
        setInterestPercentage(response?.data?.analysis?.interest_percentage || 0);
        setBehavior(response?.data?.analysis?.behavior || '');
        resetForm();
        setFileName('');
        setIsReport(true);
        setLoading(false);
      } else {
        setAuditDetails([]);
        setCallTranscript('');
        setInterestPercentage(0);
        setBehavior('');
        resetForm();
        setFileName('');
        setIsReport(false);
        toast.error('Something went wrong.');
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    resetForm
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      setFileName(file.name);
      setFieldValue('audio', file);
    } else {
      toast.error('Please upload a valid audio file.');
    }
  };

  const fetchAuditorCategoryOptions = async () => {
    try {
      const response = await dispatch(auditorCategoryListGet()).unwrap();
      if (response && response?.data?.length) {
        const options = response?.data?.map((x) => ({
          label: x.category_name,
          value: x.id
        }));
        setAuditorCategoryOption(options);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchAuditorCategoryOptions();
  }, []);

  return (
    <React.Fragment>
      <AdminHeader title="Manual Auditor" />
      <Card className="mb-2 p-2">
        <form onSubmit={handleSubmit}>
          <Row className="p-2">
            <Col lg="3">
              <FormGroup>
                <Label>File</Label>
                <div className="file-upload-container">
                  <Input
                    type="text"
                    className={`file-name ${errors['audio'] ? 'error-input' : ''}`}
                    placeholder={fileName ? fileName : 'Choose file'}
                    readOnly
                  />
                  <label className="upload-button">
                    <Icon.Upload size={18} />
                    <Input
                      type="file"
                      id="file-upload"
                      className="file-upload"
                      accept="audio/*"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
                {errors['audio'] && <span className="error-message">{errors['audio']}</span>}
              </FormGroup>
            </Col>
            <Col lg="3">
              <FormSelect
                name="category_id"
                label="Auditor category"
                placeholder="Select auditor category"
                value={values.category_id}
                options={auditorCategoryOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="lang"
                label="Language"
                placeholder="Select language"
                value={values.lang}
                options={languageSelectOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <Label></Label>
              <Button
                className="px-5 py-2 bg-primary border-primary d-flex justify-content-center align-items-center gap-2"
                type="submit"
                disabled={loading}>
                Submit
                {loading ? <Spinner size="sm" color="light" /> : null}
              </Button>
            </Col>
          </Row>
        </form>
      </Card>
      {isReport ? (
        <React.Fragment>
          <Card className="mb-2 p-2">
            <CardTitle className="px-2">Auditor Score Summary</CardTitle>
            <CardBody className="" style={{ padding: '0 4px 8px 0' }}>
              <div
                className="daily-report-table"
                // style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}
              >
                <table className="report-list-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="text-center" style={{ width: '180px' }}>
                        Weightage
                      </th>
                      <th className="text-center" style={{ width: '180px' }}>
                        Applicable Points
                      </th>
                      <th className="text-center" style={{ width: '180px' }}>
                        Earned Points
                      </th>
                      <th className="text-center" style={{ width: '180px' }}>
                        Score
                      </th>
                      <th className="text-center" style={{ width: '220px' }}>
                        Quality
                      </th>
                      <th className="text-center" style={{ width: '180px' }}>
                        Interest
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedDetails.map((item, index, array) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-light' : ''}>
                        <td>{item.category_name}</td>
                        <td className="text-center">{item.totalWeightage}</td>
                        <td className="text-center">{item.aPoints}</td>
                        <td className="text-center">{item.ePoints}</td>
                        <td className="text-center">{item.score}%</td>
                        {index === 0 && (
                          <td rowSpan={array.length} className="position-relative">
                            <div
                              className={`text-center fw-bolder fs-3 ${totalAvgScore >= 90 ? 'text-success' : totalAvgScore < 85 ? 'text-danger' : 'text-warning'}`}
                              style={{
                                marginBottom: '15px'
                              }}>
                              {totalAvgScore}%
                            </div>
                            <div
                              className="d-flex align-items-center gap-2 text-dark position-absolute"
                              style={{ fontSize: '10px', bottom: '8px' }}>
                              <span className="d-flex align-items-center justify-content-center gap-1">
                                <div
                                  className="bg-danger rounded"
                                  style={{ width: '10px', height: '10px' }}
                                />
                                {'<85%'}
                              </span>
                              <span className="d-flex align-items-center justify-content-center gap-1">
                                <div
                                  className="bg-warning rounded"
                                  style={{ width: '10px', height: '10px' }}
                                />
                                {'>=85% & <90%'}
                              </span>
                              <span className="d-flex align-items-center justify-content-center gap-1">
                                <div
                                  className="bg-success rounded"
                                  style={{ width: '10px', height: '10px' }}
                                />
                                {'>=90%'}
                              </span>
                            </div>
                          </td>
                        )}
                        {index === 0 && (
                          <td rowSpan={array.length}>
                            <div className={`text-center fw-bolder fs-3`}>
                              {interestPercentage}%
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
          {callTranscript ? (
            <Card className="mb-2 p-2">
              <CardTitle className="px-2">Call Transcript</CardTitle>
              <CardBody className="py-0 pb-2 px-0">
                <p className="px-2 m-0">{callTranscript}</p>
              </CardBody>
            </Card>
          ) : null}
          {behavior ? (
            <Card className="mb-2 p-2">
              <CardTitle className="px-2">Behavior</CardTitle>
              <CardBody className="py-0 pb-2 px-0">
                <p className="px-2 m-0">{behavior}</p>
              </CardBody>
            </Card>
          ) : null}
          <Card className="mb-2 p-2">
            <CardTitle className="px-2">Audit Details</CardTitle>
            <CardBody className="" style={{ padding: '0 4px 8px 0' }}>
              <div className="audit-report-table">
                <table className="report-list-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th style={{ width: '180px' }}>Weightage</th>
                      <th style={{ width: '360px' }}>Parameter</th>
                      <th style={{ width: '180px' }}>Audit Result</th>
                      <th style={{ width: '220px' }}>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditDetails?.map((item, catIndex) => {
                      const subcategories = item.subcategories;
                      return subcategories.map((sub, subIndex) => {
                        const isAutoAudit = process.env.REACT_APP_AUTO_AUDIT_RESULT === 'true';
                        const selectOptions = isAutoAudit
                          ? [
                              { label: 'N/A', value: 'na' },
                              { label: 'Yes', value: 'yes' },
                              { label: 'No', value: 'no' }
                            ]
                          : [
                              { label: 'N/A', value: 'na' },
                              ...Array.from({ length: sub.score_weight + 1 }, (_, i) => ({
                                label: String(i),
                                value: String(i)
                              }))
                            ];

                        return (
                          <tr
                            key={`cat-${catIndex}-sub-${subIndex}`}
                            className={
                              catIndex % 2 === 0 ? 'bg-light text-dark' : 'text-dark'
                            }>
                            {subIndex === 0 && (
                              <td rowSpan={subcategories.length}>
                                <span className="text-dark">{item.category_name}</span>
                              </td>
                            )}
                            <td>{sub.score_weight}</td>
                            <td className="text-wrap w-50">{sub.criteria_description}</td>
                            <td className="p-0">
                              <FormSelect
                                disabled={true}
                                className="table-select report-table"
                                isShowLabel={false}
                                placeholder="Select Field"
                                value={sub.applicable}
                                options={selectOptions}
                                // onChange={(e) =>
                                //   handleApplicableChange(catIndex, subIndex, e.target.value)
                                // }
                              />
                            </td>
                            <td className="p-0">
                              <FormInput
                                disabled={true}
                                isShowLabel={false}
                                className="callex-mb-0 report-table"
                                value={sub.comment}
                                name="comment"
                                // onChange={(e) =>
                                //   handleCommentChange(catIndex, subIndex, e.target.value)
                                // }
                              />
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default ManualAuditor;
